import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { fetchNewsArticlePageData } from '../../store/news/actions';
import { getMessage } from '../../store/activity-stream/selectors';
import {
  isLoadingArticle,
  hasAttemptedArticleLoad,
  getArticle,
  getNewsArticlePageLoadingError,
  hasLoadedArticle,
} from '../../store/news/selectors';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import NewsArticle from '../../components/news/article';
import SeoHead from '../../components/seo';
import clubUrl from '../../lib/routing/club-url';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import LOADING_ERROR_PROP_TYPES from '../../lib/prop-types/loading-error';

class NewsArticlePage extends React.Component {
  componentDidMount() {
    const { club, hasLoaded, loadingError, loadArticle, articleId } = this.props;

    if (!hasLoaded && !loadingError) {
      loadArticle(club.id, articleId);
    }
  }

  componentDidUpdate(prevProps) {
    const { articleId } = this.props;
    const { articleId: previousArticleId } = prevProps;
    if (articleId !== previousArticleId) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const { isLoading, previouslyLoaded } = this.props;
    if (!isLoading && !previouslyLoaded) {
      this.load();
    }
  };

  load = () => {
    const { club, loadArticle, articleId } = this.props;
    loadArticle(club.id, articleId);
  };

  title = () => {
    const { article, message } = this.props;

    return get(article, 'title', get(message, 'title', 'Loading'));
  };

  image = () => {
    const { article, message } = this.props;

    return get(article, 'image_url', get(message, 'image'));
  };

  render() {
    const {
      loadingError,
      club,
      article: {
        author,
        body_html: body,
        documents,
        quote,
        quote_author: quoteAuthor,
        tagline,
        title,
        published,
        videos,
      } = {},
      isLoading,
      articleId,
    } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} reload={this.load} />;
    }

    const shareUrl = clubUrl(club, 'news.article', { _title: title, id: articleId });

    return (
      <>
        <SeoHead
          club={club}
          title={this.title()}
          description={tagline}
          routeParams={{ _title: title, id: articleId }}
          route="news.article"
        />
        <NewsArticle
          club={club}
          author={author}
          body={body}
          documents={documents}
          quote={quote}
          quoteAuthor={quoteAuthor}
          tagline={tagline}
          title={this.title()}
          image={this.image()}
          isLoading={isLoading}
          shareProps={{
            description: tagline,
            imageUrl: this.image(),
            title,
            type: 'article',
            url: shareUrl,
          }}
          articleId={articleId}
          published={published}
          videos={videos}
        />
      </>
    );
  }
}

NewsArticlePage.getInitialProps = async ({ club, query, isServer, reduxStore, res, req }) => {
  const articleId = parseInt(query.id, 10);
  const initialProps = {
    articleId,
    activeSection: 'news',
    namespacesRequired: ['common'],
    pageType: 'news.article',
    pageUid: `news.article.${articleId}`,
  };
  if (!isServer) {
    return initialProps;
  }

  try {
    const { error } = await reduxStore.dispatch(fetchNewsArticlePageData(club.id, articleId, req));
    if (error && error.errorStatusCode && res) {
      res.statusCode = error.errorStatusCode;
    }
    return initialProps;
  } catch (err) {
    return initialProps;
  }
};

NewsArticlePage.defaultProps = {
  article: null,
  message: null,
  loadingError: null,
};

NewsArticlePage.propTypes = {
  article: PropTypes.shape({
    body_html: PropTypes.string,
    image_url: PropTypes.string,
    title: PropTypes.string,
    tagline: PropTypes.string,
    published: PropTypes.string,
  }),
  articleId: PropTypes.number.isRequired,
  club: CLUB_PROP_TYPES.isRequired,
  hasLoaded: PropTypes.bool.isRequired,
  previouslyLoaded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadArticle: PropTypes.func.isRequired,
  loadingError: LOADING_ERROR_PROP_TYPES,
  message: PropTypes.shape({
    image: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }),
};

const dispatchToProps = {
  loadArticle: fetchNewsArticlePageData,
};

const mapStateToProps = (state, ownProps) => ({
  article: getArticle(state, ownProps.articleId),
  hasLoaded: hasAttemptedArticleLoad(state, ownProps.articleId),
  isLoading: isLoadingArticle(state, ownProps.articleId),
  message: getMessage(state, `news-${ownProps.articleId}`),
  loadingError: getNewsArticlePageLoadingError(state, ownProps.articleId),
  previouslyLoaded: hasLoadedArticle(state, ownProps.articleId),
});

export default connect(mapStateToProps, dispatchToProps)(NewsArticlePage);
